// pages/LeaderboardPage.jsx
import React, { useEffect, useState } from 'react';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { fetchLeaderboard } from '../lib/gameStorage';
import { useLang } from '../i18n';

const T = {
  tr: {
    title: 'Şogi Liderlik Tablosu',
    short: 'Liderlik Tablosu',
    seoDesc: "Şogi Türkiye liderlik tablosu: bilgisayara karşı ve Arena'da kazanılan puanlara göre en iyi oyuncular.",
    lead: 'Sıralama, oyuncuların topladığı puana göre anlık güncellenir.',
    rulesTitle: 'Puan sistemi',
    ai: 'Bilgisayara karşı',
    aiRules: ['Galibiyet: Kolay +1, Orta +2, Zor +3, Usta +4', 'Mağlubiyet: 0', 'Geri alma kullanılan oyunlar sayılmaz'],
    arena: 'Şogi Arena',
    arenaRules: ['Galibiyet: +5', 'Beraberlik: +1', 'Mağlubiyet: −2'],
    error: 'Liderlik tablosu yüklenemedi.',
    empty: 'Henüz kimse oyun tamamlamadı — ilk sen ol!',
    th: ['#', 'Oyuncu', 'Puan', 'Seri', 'Maç', 'G / B / M'],
    days: '{n} gün',
  },
  en: {
    title: 'Shogi Leaderboard',
    short: 'Leaderboard',
    seoDesc: 'The Shogi Türkiye leaderboard: top players ranked by points earned against the computer and in the Arena.',
    lead: 'Rankings update live based on the points players have earned.',
    rulesTitle: 'Points',
    ai: 'Versus computer',
    aiRules: ['Win: Easy +1, Medium +2, Hard +3, Expert +4', 'Loss: 0', 'Games with undo do not count'],
    arena: 'Shogi Arena',
    arenaRules: ['Win: +5', 'Draw: +1', 'Loss: −2'],
    error: 'Could not load the leaderboard.',
    empty: 'Nobody has finished a game yet — be the first!',
    th: ['#', 'Player', 'Points', 'Streak', 'Games', 'W / D / L'],
    days: '{n} days',
  },
};

export default function LeaderboardPage() {
  const { lang, to, t } = useLang();
  const tx = T[lang];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchLeaderboard()
      .then(setRows)
      .catch((e) => {
        console.error(e);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title={tx.title}
        description={tx.seoDesc}
        jsonLd={breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: tx.short, path: to('leaderboard') }])}
      />
      <PageHeader title={tx.title} lead={tx.lead} crumbs={[{ label: tx.short }]} />
      <div className="container">
        <div className="row g-3 mb-4">
          {[[tx.ai, tx.aiRules, 'var(--c-play)'], [tx.arena, tx.arenaRules, 'var(--c-arena)']].map(([h, items, color]) => (
            <div className="col-12 col-md-6" key={h}>
              <div className="panel h-100" style={{ borderTop: `4px solid ${color}` }}>
                <h2 className="h5 font-head">{h}</h2>
                <ul className="small mb-0">{items.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="p-3 text-muted small">{t('common.loading')}</div>
            ) : error ? (
              <div className="p-3 text-danger small">{tx.error}</div>
            ) : rows.length === 0 ? (
              <div className="p-3 text-muted small">{tx.empty}</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>{tx.th[0]}</th>
                      <th>{tx.th[1]}</th>
                      <th className="text-center">{tx.th[2]}</th>
                      <th className="text-center">{tx.th[3]}</th>
                      <th className="text-center">{tx.th[4]}</th>
                      <th className="text-center">{tx.th[5]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id}>
                        <td className="fw-bold">{i < 3 ? ['🥇', '🥈', '🥉'][i] : i + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {r.photoURL && (
                              <img src={r.photoURL} alt="" width={28} height={28} className="rounded-circle" referrerPolicy="no-referrer" />
                            )}
                            <span className="fw-semibold">{r.nickname}</span>
                          </div>
                        </td>
                        <td className="text-center fw-bold fs-5">{r.score || 0}</td>
                        <td className="text-center small">🔥 {tx.days.replace('{n}', r.streak || 1)}</td>
                        <td className="text-center">{r.gamesPlayed || 0}</td>
                        <td className="text-center small">
                          <span className="text-success fw-semibold">{r.wins || 0}</span> / {r.draws || 0} / <span className="text-danger">{r.losses || 0}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
